import { useTrack } from 'src/contexts/AnalyticsContext';
import { CHATBOT_EVENTS } from 'src/lib/trackingEvents';

export function useChatAnalytics(chatState: {
  lastQuestionID: React.MutableRefObject<string>;
  likelyInternal: React.MutableRefObject<boolean>;
  timeQuestionSubmitted: React.MutableRefObject<number>;
  // Docs section the chat is running in ("" for main docs), so corex and
  // main-docs engagement can be separated in analytics.
  section?: string;
}) {
  const track = useTrack();

  const sendTrackingEvent = (event: string, payload: any) => {
    const fullTrackerObject = {
      type: event,
      payload: { ...payload, section: chatState.section ?? '' },
    };
    track(fullTrackerObject);
  };

  const trackQuestionAsked = (question: string) => {
    const payload = {
      question: question,
      qid: chatState.lastQuestionID.current,
      likelyInternal: chatState.likelyInternal.current,
    };
    sendTrackingEvent(CHATBOT_EVENTS.QUESTION_ASKED, payload);
  };

  const trackFeedbackSubmitted = (feedback: string) => {
    const payload = {
      qid: chatState.lastQuestionID.current,
      feedback: feedback,
      likelyInternal: chatState.likelyInternal.current,
    };
    sendTrackingEvent(CHATBOT_EVENTS.FEEDBACK_GIVEN, payload);
  };

  const trackEventWithTiming = (event: string) => {
    const timeDelta =
      new Date().getTime() - chatState.timeQuestionSubmitted.current;

    const payload = {
      qid: chatState.lastQuestionID.current,
      responseTime: timeDelta,
      likelyInternal: chatState.likelyInternal.current,
    };
    sendTrackingEvent(event, payload);
  };

  const trackErrorWithOptionalCode = (errCode?: string) => {
    const payload = {
      qid: chatState.lastQuestionID.current,
      likelyInternal: chatState.likelyInternal.current,
    };
    if (errCode) {
      payload['errorCode'] = errCode;
    }
    sendTrackingEvent(CHATBOT_EVENTS.ERROR_RECEIVED, payload);
  };

  const trackCopyAnswer = () => {
    const payload = {
      qid: chatState.lastQuestionID.current,
      likelyInternal: chatState.likelyInternal.current,
    };
    sendTrackingEvent(CHATBOT_EVENTS.COPY_ANSWER, payload);
  };

  const trackCopyLink = () => {
    const payload = {
      qid: chatState.lastQuestionID.current,
      likelyInternal: chatState.likelyInternal.current,
    };
    sendTrackingEvent(CHATBOT_EVENTS.COPY_LINK, payload);
  };

  const trackConversationLoaded = (conversationId: string) => {
    const payload = {
      qid: conversationId,
      likelyInternal: chatState.likelyInternal.current,
    };
    sendTrackingEvent(CHATBOT_EVENTS.CONVERSATION_LOADED, payload);
  };

  return {
    trackQuestionAsked,
    trackFeedbackSubmitted,
    trackEventWithTiming,
    trackErrorWithOptionalCode,
    trackCopyAnswer,
    trackCopyLink,
    trackConversationLoaded,
  };
}
